/**
 * Recurring items and the 12-month projection (FR-REC-01 to FR-REC-07).
 *
 * Pure. This replaces the workbook's forecast block (T:AF) and its commitments checklist
 * (P:R), and it is shaped by what those two actually are rather than by what a recurring
 * item usually is:
 *
 *  · **The forecast is a grid, not a repeat.** `Ins+MF` appears only in November,
 *    `School Fee` only in October, `EB` in two months of twelve. A model of "one amount,
 *    repeated monthly" would flatten all of that into an average that is wrong every
 *    month. So an item carries an estimate *and* optional per-month figures.
 *  · **The commitments block is a checklist that gets deleted from**, not a ledger. Items
 *    are ticked off as they are paid, which is exactly FR-REC-02's pending list: due
 *    things wait for confirmation and are never auto-posted.
 *
 * **FR-REC-03 is the central requirement of the module**: the amount is editable before
 * posting. It comes from watching the user — on 3 Aug 2026 "Sweeping/Vessels" posted at
 * 2,500 against an estimate of 3,000. An estimate that cannot be corrected on the way out
 * is a projection, not a workflow.
 */

import { daysInMonth, monthKeyOf, type DateKey, type MonthKey } from './dates';
import type { Paise } from './money';
import type { TransactionInput, TransactionType } from './transactions';

/** FR-REC-01a: all three types are first-class. The module is not expense-only. */
export type RecurringType = TransactionType;

export interface Schedule {
  /** Day of the month the item falls due. Clamped to the month's length. */
  dayOfMonth: number;
  /**
   * Months it occurs in, 1–12. Empty or absent means every month.
   *
   * This is what the workbook expressed by leaving cells blank — `School Fee` in October
   * only. Storing it as a schedule rather than as a blank cell means the projection can
   * be extended past twelve months without re-entering anything.
   */
  monthsOfYear?: number[];
  startDateKey?: DateKey;
  endDateKey?: DateKey | null;
}

export interface RecurringItem {
  id: string;
  type: RecurringType;
  description: string;
  /**
   * An **estimate**, whatever the type (FR-REC-01b). Variable income is the same case as
   * a variable expense; nothing requires an income to be constant to be recurring.
   */
  estimatePaise: Paise;
  categoryId: string | null;
  subcategoryId: string | null;
  accountId: string;
  /** Transfers only — the Jchit contribution, a card payment, an EMI. */
  toAccountId: string | null;
  schedule: Schedule;
  /**
   * Per-month figures, keyed by `MM` (01–12), overriding the estimate.
   *
   * Keyed by calendar month rather than by `YYYY-MM` so a school fee due every October
   * stays due every October, instead of being a fact about October 2026 alone.
   */
  monthlyAmounts?: Record<string, Paise>;
  isBusiness: boolean;
  archived: boolean;
  note?: string | null;
}

/** Where a due item came from. FR-REC-07: cards and loans supply their own. */
export type OccurrenceSource = 'recurring' | 'card' | 'loan';

export interface Occurrence {
  /** Stable across renders and rounds: `source:id:monthKey`. */
  key: string;
  source: OccurrenceSource;
  itemId: string;
  description: string;
  type: RecurringType;
  dueDateKey: DateKey;
  monthKey: MonthKey;
  amountPaise: Paise;
  categoryId: string | null;
  subcategoryId: string | null;
  accountId: string;
  toAccountId: string | null;
  isBusiness: boolean;
}

export function occurrenceKey(source: OccurrenceSource, itemId: string, monthKey: MonthKey): string {
  return `${source}:${itemId}:${monthKey}`;
}

// ---------------------------------------------------------------------------
// When an item falls due
// ---------------------------------------------------------------------------

/**
 * The date an item falls due in a given month.
 *
 * Clamped to the month's length, so a 31st falls on the 30th in November and the 28th in
 * February. Rolling forward into the next month instead would move a January item into
 * February and quietly skip a month.
 */
export function dueDateIn(schedule: Schedule, monthKey: MonthKey): DateKey {
  const [year, month] = monthKey.split('-').map(Number);
  const day = Math.min(Math.max(1, schedule.dayOfMonth), daysInMonth(year, month));
  return `${monthKey}-${String(day).padStart(2, '0')}`;
}

export function occursIn(item: RecurringItem, monthKey: MonthKey): boolean {
  if (item.archived) return false;

  const month = Number(monthKey.slice(5, 7));
  const months = item.schedule.monthsOfYear;
  if (months && months.length > 0 && !months.includes(month)) return false;

  const due = dueDateIn(item.schedule, monthKey);
  if (item.schedule.startDateKey && due < item.schedule.startDateKey) return false;
  if (item.schedule.endDateKey && due > item.schedule.endDateKey) return false;

  return true;
}

/**
 * What the item is expected to cost in a given month.
 *
 * A per-month figure wins over the estimate — that is how the workbook's grid survives
 * the move without being averaged away.
 */
export function amountIn(item: RecurringItem, monthKey: MonthKey): Paise {
  const month = monthKey.slice(5, 7);
  return item.monthlyAmounts?.[month] ?? item.estimatePaise;
}

export function occurrenceFor(item: RecurringItem, monthKey: MonthKey): Occurrence | null {
  if (!occursIn(item, monthKey)) return null;

  return {
    key: occurrenceKey('recurring', item.id, monthKey),
    source: 'recurring',
    itemId: item.id,
    description: item.description,
    type: item.type,
    dueDateKey: dueDateIn(item.schedule, monthKey),
    monthKey,
    amountPaise: amountIn(item, monthKey),
    categoryId: item.categoryId,
    subcategoryId: item.subcategoryId,
    accountId: item.accountId,
    toAccountId: item.toAccountId,
    isBusiness: item.isBusiness,
  };
}

export function occurrencesIn(items: RecurringItem[], monthKey: MonthKey): Occurrence[] {
  return items
    .map((item) => occurrenceFor(item, monthKey))
    .filter((o): o is Occurrence => o !== null);
}

// ---------------------------------------------------------------------------
// FR-REC-02 — the "Due now" list
// ---------------------------------------------------------------------------

export interface DueOptions {
  /** Occurrence keys already posted or dismissed. */
  handled?: Set<string>;
  /** How far ahead to look. A week by default: due "now" means soon, not today only. */
  lookaheadDays?: number;
}

/**
 * What is waiting for confirmation, as at `today`.
 *
 * Includes anything already overdue, however long ago — an unpaid item does not stop
 * being due because the month turned, and dropping it off the list is how a bill gets
 * missed. Sorted oldest first for the same reason.
 *
 * **Never auto-posted** (FR-REC-02). This function reports; posting is a user action.
 */
export function dueNow(
  occurrences: Occurrence[],
  today: DateKey,
  { handled = new Set(), lookaheadDays = 7 }: DueOptions = {},
): Occurrence[] {
  const horizon = addDaysToKey(today, lookaheadDays);

  return occurrences
    .filter((o) => !handled.has(o.key) && o.dueDateKey <= horizon)
    .sort((a, b) => a.dueDateKey.localeCompare(b.dueDateKey) || a.description.localeCompare(b.description));
}

/** Local, so this module needs nothing from the date module beyond month arithmetic. */
function addDaysToKey(dateKey: DateKey, days: number): DateKey {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

export function isOverdue(occurrence: Occurrence, today: DateKey): boolean {
  return occurrence.dueDateKey < today;
}

/**
 * Confirming an occurrence creates a normal transaction (FR-REC-04).
 *
 * `amountPaise` is passed separately rather than read off the occurrence, because the
 * whole point of FR-REC-03 is that what posts may differ from what was estimated. The
 * estimate stays on the item, untouched — that is the second half of the exit criterion:
 * posting 2,500 against a 3,000 estimate records 2,500 **and keeps the estimate**.
 */
export function toTransactionInput(
  occurrence: Occurrence,
  amountPaise: Paise,
  dateKey: DateKey = occurrence.dueDateKey,
): TransactionInput {
  return {
    type: occurrence.type,
    dateKey,
    accountId: occurrence.accountId,
    toAccountId: occurrence.type === 'transfer' ? occurrence.toAccountId : null,
    amountPaise,
    categoryId: occurrence.type === 'transfer' ? null : occurrence.categoryId,
    subcategoryId: occurrence.type === 'transfer' ? null : occurrence.subcategoryId,
    description: occurrence.description,
    isBusiness: occurrence.isBusiness,
  };
}

// ---------------------------------------------------------------------------
// FR-REC-05, FR-REC-06 — the 12-month projection
// ---------------------------------------------------------------------------

export interface ProjectedMonth {
  monthKey: MonthKey;
  incomePaise: Paise;
  expensePaise: Paise;
  transferPaise: Paise;
  netPaise: Paise;
  occurrences: Occurrence[];
}

/**
 * The projection (FR-REC-05).
 *
 * **Income is summed per month from that month's own occurrences** — which is the whole
 * of FR-REC-06. The workbook's forecast used an absolute `SUM($U$37:$U$44)` in all twelve
 * month columns, so projected income was identical every month regardless of input
 * (defect 4). Here a January bonus raises January and nothing else, because there is no
 * shared cell to point at.
 */
export function project(
  items: RecurringItem[],
  monthKeys: MonthKey[],
  extra: Occurrence[] = [],
): ProjectedMonth[] {
  const byMonth = new Map<MonthKey, Occurrence[]>();
  for (const occurrence of extra) {
    byMonth.set(occurrence.monthKey, [...(byMonth.get(occurrence.monthKey) ?? []), occurrence]);
  }

  return monthKeys.map((monthKey) => {
    const occurrences = [...occurrencesIn(items, monthKey), ...(byMonth.get(monthKey) ?? [])];

    let incomePaise = 0;
    let expensePaise = 0;
    let transferPaise = 0;

    for (const occurrence of occurrences) {
      if (occurrence.type === 'income') incomePaise += occurrence.amountPaise;
      else if (occurrence.type === 'expense') expensePaise += occurrence.amountPaise;
      else transferPaise += occurrence.amountPaise;
    }

    return {
      monthKey,
      incomePaise,
      expensePaise,
      transferPaise,
      // Transfers are excluded: a card payment or a chit contribution moves money between
      // the user's own accounts and is neither earned nor spent (FR-TXN-05).
      netPaise: incomePaise - expensePaise,
      occurrences,
    };
  });
}

export function projectionTotals(months: ProjectedMonth[]): Omit<ProjectedMonth, 'monthKey' | 'occurrences'> {
  return months.reduce(
    (total, month) => ({
      incomePaise: total.incomePaise + month.incomePaise,
      expensePaise: total.expensePaise + month.expensePaise,
      transferPaise: total.transferPaise + month.transferPaise,
      netPaise: total.netPaise + month.netPaise,
    }),
    { incomePaise: 0, expensePaise: 0, transferPaise: 0, netPaise: 0 },
  );
}

// ---------------------------------------------------------------------------
// FR-REC-07 — cards and loans supply their own
// ---------------------------------------------------------------------------

export interface CardDue {
  accountId: string;
  name: string;
  outstandingPaise: Paise;
  dueDay: number;
  /** A due day earlier than the statement day means the bill falls due next month. */
  dueDayIsNextMonth?: boolean;
  paymentAccountId: string | null;
}

/**
 * Card bills as occurrences (FR-REC-07).
 *
 * Built from the card accounts rather than from a recurring item the user defines by
 * hand — a hand-defined duplicate would post the bill twice, and would carry a stale
 * estimate rather than the actual outstanding.
 *
 * A card with nothing outstanding produces nothing: there is no bill to pay.
 */
export function cardOccurrences(cards: CardDue[], monthKey: MonthKey): Occurrence[] {
  return cards
    .filter((card) => card.outstandingPaise > 0)
    .map((card) => ({
      key: occurrenceKey('card', card.accountId, monthKey),
      source: 'card' as const,
      itemId: card.accountId,
      description: `${card.name} bill`,
      type: 'transfer' as const,
      dueDateKey: dueDateIn({ dayOfMonth: card.dueDay }, monthKey),
      monthKey,
      amountPaise: card.outstandingPaise,
      categoryId: null,
      subcategoryId: null,
      accountId: card.paymentAccountId ?? '',
      toAccountId: card.accountId,
      isBusiness: false,
    }));
}

export interface LoanDue {
  loanId: string;
  accountId: string;
  name: string;
  dueDateKey: DateKey;
  amountPaise: Paise;
  paymentAccountId: string | null;
}

/** Loan EMIs as occurrences, read from the lender's schedule (FR-REC-07, FR-LOAN-05). */
export function loanOccurrences(dues: LoanDue[]): Occurrence[] {
  return dues.map((due) => ({
    key: occurrenceKey('loan', due.loanId, monthKeyOf(due.dueDateKey)),
    source: 'loan' as const,
    itemId: due.loanId,
    description: `${due.name} EMI`,
    type: 'transfer' as const,
    dueDateKey: due.dueDateKey,
    monthKey: monthKeyOf(due.dueDateKey),
    amountPaise: due.amountPaise,
    categoryId: null,
    subcategoryId: null,
    accountId: due.paymentAccountId ?? '',
    toAccountId: due.accountId,
    isBusiness: false,
  }));
}
